/**
 * The WhatsApp vocabulary: what a category is for, what Meta's limits are, what
 * an approval state means, and what a rejection is *about* in plain words.
 *
 * Same rule as `template-display.ts` and `integration-display.ts`: **it invents
 * no claim.** Whether a document is submittable, what Meta refused it for and
 * what the proposed repair is are all answers `@wend/shared` and the API give;
 * this file decides the wording around them. Every limit below is imported from
 * `@wend/shared` rather than retyped — the character ceilings from
 * `contracts/whatsapp.ts`, and from slice 9 the per-format media ceilings from
 * `contracts/whatsapp-media.ts` — so a screen counting characters and an API
 * refusing them cannot drift apart.
 *
 * **The media *refusal* sentences are not here, and must not be added.**
 * `describeWaMediaRejection` in `@wend/shared` writes them, because a refusal
 * has to quote the limit that was actually applied and the check that applied it
 * is the same function. What this file adds is the sentence stated *before*
 * anybody picks a file, which is a different job: a limit discovered by being
 * refused is a limit that was not shown.
 *
 * Three words are never used on these screens: **spend, cost, and any currency
 * symbol.** Meta bills the tenant per 24-hour conversation, on the tenant's own
 * Meta account; what Wend meters is credits. Putting a price on this screen
 * would be Wend quoting a number it does not charge and cannot know.
 */
import {
  WA_BODY_MAX_LENGTH,
  WA_BUTTON_LABEL_MAX_LENGTH,
  WA_BUTTON_URL_MAX_LENGTH,
  WA_FOOTER_MAX_LENGTH,
  WA_HEADER_TEXT_MAX_LENGTH,
  WA_MAX_BUTTONS,
  WA_MEDIA_LIMITS,
  formatWaMediaBytes,
  type MetaRejectionReason,
  type TemplateStatus,
  type WaButtonType,
  type WaHeaderFormat,
  type WaMediaFormat,
  type WaProposedFixKind,
  type WaTemplateCategory,
} from '@wend/shared';
import type { BadgeTone } from '@wend/ui';

import type { MetaFailureCode, MetaReadinessState, MetaTokenExpiryState } from './api/whatsapp';

/* -------------------------------------------------------------------------- */
/* Category                                                                    */
/* -------------------------------------------------------------------------- */

export interface WaCategoryDisplay {
  readonly label: string;
  /** One line saying what belongs in it. */
  readonly purpose: string;
  /** The consequence of choosing it, which is what people actually get wrong. */
  readonly consequence: string;
}

/**
 * One entry per member of `WA_TEMPLATE_CATEGORY_VALUES`.
 *
 * The category is the single most common reason a template comes back rejected,
 * and the reason is nearly always the same: an offer written as a delivery
 * update. Meta recategorises rather than believing the label, so the wording
 * here is about what the *content* has to be, not about which box to tick.
 */
export const WA_CATEGORY_DISPLAY: Readonly<Record<WaTemplateCategory, WaCategoryDisplay>> = {
  marketing: {
    label: 'Marketing',
    purpose: 'Offers, launches, invitations, anything promotional.',
    consequence:
      'Goes only to people who opted in to marketing, and opens a marketing conversation when it lands.',
  },
  utility: {
    label: 'Utility',
    purpose: 'A follow-up to something the person did — an order, a booking, a request.',
    consequence:
      'Meta reads the content, not the label. Anything promotional in a utility template is recategorised or rejected.',
  },
  authentication: {
    label: 'Authentication',
    purpose: 'One-time codes, and nothing else.',
    consequence:
      'Meta formats these itself and refuses anything that is not a code. Wend does not send codes — this is here for completeness.',
  },
};

/* -------------------------------------------------------------------------- */
/* Header, buttons                                                             */
/* -------------------------------------------------------------------------- */

export const WA_HEADER_FORMAT_LABEL: Readonly<Record<WaHeaderFormat, string>> = {
  text: 'Text',
  image: 'Image',
  document: 'Document',
  video: 'Video',
};

/**
 * Why a media header is a file picker on this screen — and what it was before.
 *
 * **SUPERSEDED IN SLICE 9, AND THE SUPERSEDED SENTENCE IS THE POINT.** This
 * constant used to end *"…the template stores the handle Meta gives back —
 * paste that handle here"*, which described slice 7 exactly: there was no upload
 * path anywhere in Wend, so the composer offered a text box and a person was
 * expected to arrive holding a handle minted somewhere else. Left standing, that
 * sentence argues for the one arrangement this slice exists to remove — a
 * template carrying a handle whose bytes Wend never had. A handle cannot be
 * re-minted from itself, so such a template can never be resubmitted after a
 * rejection; the person has to find the original file again.
 *
 * What survives unchanged, and is why the note still exists: **WhatsApp has no
 * attachments.** A file here is a *header*, not something travelling beside the
 * message, and saying so is cheaper than the support ticket from somebody
 * looking for a paperclip.
 */
export const WA_MEDIA_HEADER_NOTE =
  'WhatsApp has no attachments. A media header is a file Wend stores and uploads to Meta as the sample it reviews — choose one below rather than pasting a handle.';

/** `an image`, `a document`. Derived, so a fourth format needs no edit here. */
function anArticle(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

/**
 * The display words for a set of MIME types, taken from their subtypes.
 *
 * `image/jpeg` → JPEG, `application/pdf` → PDF, `video/mp4` → MP4. Derived
 * rather than looked up in a table, because a table of friendly names beside the
 * contract's list of types is two lists that can disagree — and the way they
 * would disagree is that Meta widens the accepted set and the new type has no
 * name here, so the sentence silently stops mentioning it.
 */
function mediaTypeWords(mimeTypes: readonly string[]): string {
  const words = mimeTypes.map((type) => (type.split('/')[1] ?? type).toUpperCase());
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1] ?? ''}`;
}

/**
 * What this format's picker will accept, stated **before** anybody uses it.
 *
 * The three formats have three different ceilings and three different accepted
 * sets, and none of them is slice 4's email attachment limit — so one sentence
 * covering "media" would be wrong for at least two of them. The composer shows
 * the line for the format actually selected.
 *
 * **Every figure is read out of `WA_MEDIA_LIMITS`**, never typed here, so this
 * sentence and the check that refuses a file quote the same numbers. That is the
 * rule `asset-upload-error.interceptor.ts` exists for: quoting a limit that is
 * not the limit applied is both wrong and unactionable.
 *
 * **Whose number it is decides what the sentence may claim.** Where Wend holds a
 * figure narrower than Meta's — a document, capped at 20 MB against Meta's 100 —
 * the sentence says so and says whose is whose, because attributing our own
 * caution to WhatsApp is a confident claim about a provider that is not true.
 *
 * The wording follows `describeWaMediaRejection` deliberately: *"WhatsApp's
 * documented limit for an image is 5 MB"*, not *"WhatsApp refuses an image
 * header over 5 MB"*. Meta publishes those figures for media on the messaging
 * endpoints and publishes **no** size ceiling for the Resumable Upload that
 * mints a header handle, so applying them there is a documented assumption. A
 * screen may state the documented figure; it may not state our assumption about
 * a provider's endpoint as fact.
 */
export function waMediaLimitLine(format: WaMediaFormat): string {
  const limits = WA_MEDIA_LIMITS[format];
  const cap = formatWaMediaBytes(limits.maxBytes);
  const types = mediaTypeWords(limits.mimeTypes);

  if (limits.limitOwner === 'meta') {
    return `${types}, up to ${cap} — WhatsApp’s documented limit for ${anArticle(limits.noun)} ${limits.noun}.`;
  }
  return `${types}, up to ${cap}. WhatsApp’s own documented limit is ${formatWaMediaBytes(limits.metaMaxBytes)}; the smaller figure is Wend’s, because the whole file is held in memory while it is checked and uploaded.`;
}

export const WA_BUTTON_TYPE_LABEL: Readonly<Record<WaButtonType, string>> = {
  quick_reply: 'Quick reply',
  url: 'Link',
};

export const WA_BUTTON_TYPE_HELP: Readonly<Record<WaButtonType, string>> = {
  quick_reply:
    'A tappable reply. The person’s tap comes back as an inbound message — which also opens a 24-hour conversation.',
  url: 'Opens a link. https:// only, and a variable may only sit at the very end of the URL.',
};

/* -------------------------------------------------------------------------- */
/* Meta's limits, as the composer states them                                  */
/* -------------------------------------------------------------------------- */

/**
 * The character ceilings, imported rather than retyped.
 *
 * They are shown as a live count next to each field, because Meta's are strict
 * and unexplained — a body cut at 1,024 characters is refused outright, not
 * truncated — and finding that out from a 400 after a save is a poor way to
 * learn it.
 */
export const WA_LIMITS = {
  header: WA_HEADER_TEXT_MAX_LENGTH,
  body: WA_BODY_MAX_LENGTH,
  footer: WA_FOOTER_MAX_LENGTH,
  buttonLabel: WA_BUTTON_LABEL_MAX_LENGTH,
  buttonUrl: WA_BUTTON_URL_MAX_LENGTH,
  buttons: WA_MAX_BUTTONS,
} as const;

/** `312 / 1024` — and whether it is over. */
export function remaining(value: string, limit: number): { used: number; over: boolean } {
  const used = [...value].length;
  return { used, over: used > limit };
}

/* -------------------------------------------------------------------------- */
/* Approval status                                                             */
/* -------------------------------------------------------------------------- */

export interface WaStatusDisplay {
  readonly label: string;
  /** What the state means for the person looking at it, in one line. */
  readonly meaning: string;
}

/**
 * The four states a WhatsApp template can be in, from Wend's own enum.
 *
 * `approved` is the only one a campaign may pick, and the wording says which
 * party did the approving in each case — for email a tenant approves their own,
 * and someone moving between the two screens should never have to guess whether
 * Meta has seen this.
 *
 * Words only: the badge colour is `TEMPLATE_STATUS_TONE` in
 * `components/TemplateStatusBadge.tsx`, shared with the email editor since
 * slice 66. The `tone` field this table used to carry (`info` for "With Meta",
 * where the badge now says `warn`) was deleted rather than left to disagree.
 */
export const WA_STATUS_DISPLAY: Readonly<Record<TemplateStatus, WaStatusDisplay>> = {
  draft: {
    label: 'Draft',
    meaning: 'Not sent to Meta yet. Nothing can be sent on a draft.',
  },
  pending: {
    label: 'With Meta',
    meaning:
      'Submitted and waiting on Meta’s review. Meta usually answers in minutes, sometimes in a day. Wend updates this when Meta’s webhook arrives — there is nothing to press.',
  },
  approved: {
    label: 'Approved by Meta',
    meaning: 'Meta accepted it. A WhatsApp campaign can now use this template.',
  },
  rejected: {
    label: 'Rejected by Meta',
    meaning:
      'Meta refused it. Read the reason, fix the template, and resubmit — a resubmission is an explicit action, never automatic.',
  },
};

/**
 * Why nothing resubmits on its own.
 *
 * Shown next to the resubmit control, because the obvious feature request when
 * looking at a rejection is a retry button — and a loop of automatic
 * resubmissions against Meta is one of the ways a WABA gets rate limited or
 * flagged. The rule is worth stating where it would otherwise be asked for.
 */
export const WA_RESUBMIT_IS_MANUAL =
  'Wend never resubmits a template on its own. A rejection answered by an automatic retry is how a WhatsApp Business Account gets flagged, so a resubmission needs a person who has read Meta’s reason.';

/* -------------------------------------------------------------------------- */
/* Rejection reasons                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The heading above Meta's own sentence.
 *
 * **Headings, not translations.** Meta's reason is printed verbatim underneath;
 * restating it here would give a person two accounts of the same refusal that
 * can disagree. What this adds is which of the seven it is, at a glance.
 */
export const META_REJECTION_REASON_TITLE: Readonly<Record<MetaRejectionReason, string>> = {
  abusive_content: 'Meta judged the content abusive',
  incorrect_category: 'Wrong category for this content',
  invalid_format: 'The template’s format was refused',
  scam: 'Meta judged this a scam risk',
  promotional: 'Promotional content outside Marketing',
  tag_content_mismatch: 'The content does not match the category',
  none: 'Meta gave no specific reason',
};

/** The heading above the proposed repair, by what kind of repair it is. */
export const WA_PROPOSED_FIX_TITLE: Readonly<Record<WaProposedFixKind, string>> = {
  recategorise: 'Proposed fix — change the category',
  edit_content: 'Proposed fix — edit the content',
  no_proposal: 'No fix proposed',
};

/* -------------------------------------------------------------------------- */
/* The Meta account                                                            */
/* -------------------------------------------------------------------------- */

export interface MetaReadinessDisplay {
  readonly label: string;
  readonly tone: BadgeTone;
  readonly meaning: string;
}

export const META_READINESS_DISPLAY: Readonly<Record<MetaReadinessState, MetaReadinessDisplay>> = {
  ready: {
    label: 'Ready to send',
    tone: 'good',
    meaning: 'The number is verified, the account is reviewed, and Meta accepted the token.',
  },
  number_not_verified: {
    label: 'Number not verified',
    tone: 'bad',
    meaning:
      'Meta has not verified this phone number, so it cannot send. Finish verification in WhatsApp Manager, then test again.',
  },
  quality_flagged: {
    label: 'Quality flagged',
    tone: 'bad',
    meaning:
      'Meta has rated this number’s quality red. Its messaging limit is likely to be cut, and it can be paused outright — send less, and only to people expecting it.',
  },
  waba_pending_review: {
    label: 'Account under review',
    tone: 'warn',
    meaning:
      'Meta is still reviewing this WhatsApp Business Account. Templates can be submitted, but sending is restricted until the review finishes.',
  },
};

/**
 * The token expiry states, and what each one costs whoever is reading.
 *
 * **This is the risk this screen exists for.** A system user token that quietly
 * expires does not fail loudly: templates stop submitting and campaigns stop
 * sending, and the first symptom is usually a campaign that reached nobody. So
 * the expiry is a headline on the WhatsApp tab rather than a field in a list,
 * and the "never" state is stated as plainly as the other two — a token with no
 * expiry is a decision somebody made, not the absence of information.
 *
 * **`never.meaning` is a claim about a `null` expiry and must not be printed for
 * a dated one.** Slice 16 amends this entry, because `metaTokenExpiry` returns
 * `never` for *anything* outside the warning window — including a token that
 * expires in six months, which very much has an expiry. The old card printed
 * this sentence with that date and a countdown beside it, which is two claims
 * and one of them false. `WhatsAppSendState` now branches on `expiresAt` first
 * and reaches this entry only when there is genuinely no date. The third caller
 * of the map, the tab status in `whatsapp-reading.ts`, does not use this
 * sentence at all.
 *
 * The two amber states are also not interchangeable, and the wording is what
 * keeps them apart: `expired` states a stoppage that has already happened,
 * `expiring` urges a replacement before one does. They prompt different
 * urgency, so a screen that rendered them in one sentence with two colours
 * would have thrown away the distinction this map exists to carry.
 */
export const META_TOKEN_EXPIRY_DISPLAY: Readonly<
  Record<MetaTokenExpiryState, MetaReadinessDisplay>
> = {
  never: {
    label: 'No expiry',
    tone: 'good',
    meaning:
      'Meta reports no expiry for this token. That is what a system user token should look like.',
  },
  expiring: {
    label: 'Expiring soon',
    tone: 'warn',
    meaning:
      'This token expires shortly. Replace it before it does: once it has expired, template submissions and every WhatsApp campaign stop, and the first sign is usually a campaign that reached nobody.',
  },
  expired: {
    label: 'Expired',
    tone: 'bad',
    meaning:
      'This token has expired. Nothing can be submitted to Meta and nothing can be sent on WhatsApp until it is replaced.',
  },
};

/** Said when Meta would not tell us anything about the token's lifetime. */
export const META_TOKEN_NOT_INTROSPECTED =
  'Meta would not say when this token expires — debugging a token needs an app token this connection does not have. Treat the expiry as unknown and check it in Meta’s Business Settings.';

/** The heading over a failed check. Meta's own message follows it. */
export const META_FAILURE_HEADING: Readonly<Record<MetaFailureCode, string>> = {
  invalid_token: 'Meta refused the token',
  token_expired: 'The token has expired',
  access_denied: 'The token is missing a permission',
  unknown_object: 'Meta does not recognise that id',
  waba_not_approved: 'This WhatsApp Business Account cannot send yet',
  rate_limited: 'Meta is rate limiting this account',
  network: 'Wend could not reach Meta',
  unknown: 'Meta refused the check',
};

/**
 * Said, in full, whenever a stub answered.
 *
 * The same rule the SES card follows: a green from a fake transport must never
 * be readable as a green from the real one. Here it matters more, because the
 * thing on the other end of a real check is a phone in someone's hand.
 */
export const META_STUB_NOTICE =
  'This check did not reach Meta. A stub transport answered it, so everything above is fabricated — no WhatsApp account has been verified and nothing can be sent on this connection.';

/**
 * What the conversation-based billing means for whoever reads this screen.
 *
 * Stated because "per message" is the assumption everyone arrives with, and it
 * is wrong in a way that changes how a campaign is planned: a reply, a quick
 * reply tap, and every message inside the following 24 hours all sit inside one
 * conversation on the tenant's Meta account.
 */
export const WA_CONVERSATION_NOTE =
  'Meta meters WhatsApp by 24-hour conversation, not by message: one template message opens a conversation, and replies inside that window sit within it. Wend meters your credits per message sent, which is a different unit from Meta’s — the two are not comparable line for line.';
